import {
  getIanaTzName,
  getBrowserTimezoneId,
  utcMinutesToHHMM,
  hhmmToUTCMinutes
} from '@util/helpers';
import { isEmpty } from 'lodash-es';

export function updateFormWithUserData(form, user, tenant, people) {
  form.reset({
    context: user?.context || '',
    tenant_context: tenant?.card_config?.context || '',
    title: user?.title || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    department: user?.departments?.[0] || '',
    time_zone_id: user?.time_zone_id || getBrowserTimezoneId(),
    morning_digest_enabled: user?.morning_digest_enabled ?? true,
    morning_digest_time: utcMinutesToHHMM(
      user?.digest_configs?.morning?.time_of_day ?? 720,
      getIanaTzName(user?.time_zone_id)
    ),
    evening_digest_enabled: user?.evening_digest_enabled ?? true,
    evening_digest_time: utcMinutesToHHMM(
      user?.digest_configs?.evening?.time_of_day ?? 1380,
      getIanaTzName(user?.time_zone_id)
    ),
    users: people?.data?.map((p) => {
      return { email: p.email, type: p.type, id: p.id, isExisting: true };
    }),
    tenant: { name: tenant?.name || '', id: tenant?.id || '' },
    auto_schedule: user?.meeting_config?.auto_schedule || false
  });
}

/**
 *  onStepSubmit Handers
 */

/**
 * Handler for Profile Setup step submission
 * @param {Object} params
 * @param {Object} params.formValues - Form values from react-hook-form
 * @param {Object} params.user - Current user object
 * @param {Object} params.tenant - Current tenant object
 * @param {Function} params.patchPerson - Mutation function to update person
 * @param {Function} params.patchTenantAsAppUser - Mutation function to update tenant
 * @param {Function} params.setUser - Function to update user in store
 * @param {Function} params.refreshSessionTenant - Function to refresh session tenant
 * @param {Function} params.toast - Toast notification function
 */
export async function handleProfileSetupSubmit({
  formValues,
  user,
  tenant,
  patchPerson,
  patchTenantAsAppUser,
  setUser,
  refreshSessionTenant,
  toast
}) {
  if (formValues.tenant?.name !== tenant?.name) {
    await patchTenantAsAppUser({
      id: tenant?.id,
      name: formValues.tenant?.name
    });
  }
  const patchBody = {
    first_name: formValues.first_name,
    last_name: formValues.last_name,
    title: formValues.title,
    departments: formValues.department ? [formValues.department] : []
  };
  const updatedUser = await patchPerson(
    {
      id: user?.id,
      ...patchBody
    },
    {
      onError: (e) => {
        toast({
          status: 'error',
          title: 'error patching user',
          description: e?.message
        });
      }
    }
  );
  setUser(updatedUser);
  await refreshSessionTenant(tenant?.id);
}

/**
 * Handler for Scheduling step submission
 * @param {Object} params
 * @param {Object} params.formValues - Form values from react-hook-form
 * @param {Object} params.user - Current user object
 * @param {Function} params.patchPerson - Mutation function to update person
 * @param {Function} params.setUser - Function to update user in store
 * @param {Function} params.toast - Toast notification function
 */
export async function handleSchedulingSubmit({ formValues, user, patchPerson, setUser, toast }) {
  const updatedUser = await patchPerson(
    {
      id: user?.id,
      meeting_config: {
        ...user?.meeting_config,
        auto_schedule: formValues.auto_schedule
      }
    },
    {
      onError: (e) => {
        toast({
          status: 'error',
          title: 'error patching user',
          description: e?.message
        });
      }
    }
  );
  setUser(updatedUser);
}

/**
 * Handler for User Settings step submission
 * @param {Object} params
 * @param {Object} params.formValues - Form values from react-hook-form
 * @param {Object} params.user - Current user object
 * @param {Function} params.patchPerson - Mutation function to update person
 * @param {Function} params.setUser - Function to update user in store
 * @param {Function} params.toast - Toast notification function
 */
export async function handleUserSettingsSubmit({ formValues, user, patchPerson, setUser, toast }) {
  const configuredTimezone = getIanaTzName(formValues.time_zone_id);

  const morningTime = hhmmToUTCMinutes(formValues.morning_digest_time, configuredTimezone);
  const eveningTime = hhmmToUTCMinutes(formValues.evening_digest_time, configuredTimezone);

  const patchBody = {
    time_zone_id: formValues.time_zone_id,
    digest_configs: {}
  };
  if (formValues.morning_digest_enabled) {
    patchBody.digest_configs.morning = { time_of_day: morningTime };
  }
  if (formValues.evening_digest_enabled) {
    patchBody.digest_configs.evening = { time_of_day: eveningTime };
  }

  // Remove digest_configs if it's empty
  if (isEmpty(patchBody.digest_configs)) {
    delete patchBody.digest_configs;
  }

  const updatedUser = await patchPerson(
    {
      id: user?.id,
      ...patchBody
    },
    {
      onError: (e) => {
        toast({
          status: 'error',
          title: 'error patching user',
          description: e?.message
        });
      }
    }
  );
  setUser(updatedUser);
}

/**
 * Handler for AI Context (Prompts) step submission
 * @param {Object} params
 * @param {Object} params.formValues - Form values from react-hook-form
 * @param {Object} params.user - Current user object
 * @param {Object} params.tenant - Current tenant object
 * @param {Function} params.patchPerson - Mutation function to update person
 * @param {Function} params.patchTenantAsAppUser - Mutation function to update tenant
 * @param {Function} params.setUser - Function to update user in store
 * @param {Function} params.refreshSessionTenant - Function to refresh session tenant
 * @param {Function} params.toast - Toast notification function
 */
export async function handlePromptsSubmit({
  formValues,
  user,
  tenant,
  patchPerson,
  patchTenantAsAppUser,
  setUser,
  refreshSessionTenant,
  toast
}) {
  if (formValues.tenant_context) {
    await patchTenantAsAppUser({
      id: tenant?.id,
      card_config: {
        ...tenant?.card_config,
        context: formValues.tenant_context
      }
    });
  }

  if (formValues.context !== user?.context) {
    const updatedUser = await patchPerson(
      {
        id: user?.id,
        context: formValues.context
      },
      {
        onError: (e) => {
          toast({
            status: 'error',
            title: 'error patching user',
            description: e?.message
          });
        }
      }
    );
    setUser(updatedUser);
  }
  await refreshSessionTenant(tenant?.id);
}

/**
 * Handler for Add Users step submission
 * @param {Object} params
 * @param {Object} params.formValues - Form values from react-hook-form
 * @param {Function} params.createPersonWithInvite - Mutation function to create person with invite
 * @param {Function} params.toast - Toast notification function
 */
export async function handleAddUsersSubmit({ formValues, createPersonWithInvite, toast }) {
  const newUsers = formValues.users.filter((u) => !u.isExisting);
  const errors = [];
  for (const user of newUsers) {
    try {
      await createPersonWithInvite({
        email: user?.email,
        type: user?.type
      });
    } catch (e) {
      errors.push({
        email: user?.email,
        message: JSON.stringify(e?.response?.data?.errors)
      });
    }
  }
  if (errors.length) {
    for (const error in errors) {
      toast({
        status: 'error',
        title: `Error adding ${errors[error].email}`,
        description: errors[error].message
      });
    }
    throw new Error('Error adding some users');
  }
}
